import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function TermsOfService() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Nav */}
      <nav className="bg-slate-900 border-b border-yellow-500/20 px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="cursor-pointer inline-block">
          <img
            src="https://static.readdy.ai/image/870e0649015e5e428345464cd8040611/e2821367afaf48576bf0fa4cd75aa99b.png"
            alt="Guardian Veterans LLC"
            className="h-[48px] w-auto object-contain"
          />
        </button>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base"></i>
          Back to Home
        </button>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="w-12 h-12 flex items-center justify-center bg-yellow-500/20 rounded-full mx-auto mb-4">
            <i className="ri-file-list-3-line text-yellow-400 text-2xl"></i>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Terms of Service</h1>
          <p className="text-slate-400 text-sm">Last updated: March 22, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-14 text-slate-700 leading-relaxed space-y-10">

        <section>
          <p className="text-base">
            These Terms of Service ("Terms") govern your use of the website{' '}
            <a href="https://gvnemt.com" className="text-yellow-600 hover:underline">gvnemt.com</a>{' '}
            and the transportation services provided by Guardian Veterans LLC ("we," "our," or "us"), a
            veteran-owned Non-Emergency Medical Transportation (NEMT) company based in Waddell, Arizona.
            By using our website or scheduling a ride, you agree to these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">1. Services Provided</h2>
          <p className="text-sm mb-3">
            Guardian Veterans LLC provides Non-Emergency Medical Transportation (NEMT) services in the West
            Valley Arizona area, including but not limited to:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Wheelchair-accessible vehicle (WAV) transportation</li>
            <li>Ambulatory (non-wheelchair) transportation</li>
            <li>Private-pay transportation services</li>
            <li>Facility-contracted transportation coordination</li>
          </ul>
          <p className="mt-3 text-sm">
            We reserve the right to modify, suspend, or discontinue any service at any time without prior notice.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">2. Eligibility &amp; Booking</h2>
          <p className="text-sm mb-3">By scheduling a ride, you represent and agree that:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>You are 18 years of age or older, or are booking on behalf of someone with proper authority.</li>
            <li>All information you provide (name, address, appointment details) is accurate and complete.</li>
            <li>You understand that NEMT services are for non-emergency, medically-related transportation only. If you are experiencing a medical emergency, call 911.</li>
            <li>You will notify us of any changes to your ride request at least 24 hours in advance when possible.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">3. Cancellations &amp; No-Shows</h2>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Please provide as much advance notice as possible if you need to cancel or reschedule.</li>
            <li>Repeated no-shows or last-minute cancellations may affect your ability to schedule future rides.</li>
            <li>Cancellation fees, if any, will be communicated at the time of booking.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">4. Passenger Conduct</h2>
          <p className="text-sm mb-3">To ensure a safe and respectful environment for all passengers and drivers, you agree to:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Treat drivers and staff with courtesy and respect at all times.</li>
            <li>Wear a seatbelt and comply with all vehicle safety requirements.</li>
            <li>Not bring prohibited substances, weapons, or items that may endanger others.</li>
            <li>Notify us of any special needs (oxygen equipment, service animals, etc.) prior to your ride.</li>
          </ul>
          <p className="mt-3 text-sm">
            We reserve the right to refuse or terminate a ride if passenger conduct poses a safety risk to the driver or others.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">5. Payment &amp; Billing</h2>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Guardian Veterans LLC operates on a private-pay basis. All rates will be communicated clearly prior to booking and are due as agreed upon at scheduling.</li>
            <li>We accept payment by cash, check, or other methods agreed upon at the time of booking.</li>
            <li>Private-pay rates are subject to change; any changes will be communicated before your scheduled ride.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">6. Limitation of Liability</h2>
          <p className="text-sm">
            Guardian Veterans LLC shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services or website. Our total liability for any claim arising out of or relating to these Terms shall not exceed the amount paid by you for the specific service giving rise to the claim.
          </p>
          <p className="mt-3 text-sm">
            We are not responsible for delays caused by traffic, weather, mechanical issues, or other circumstances beyond our control. We will make every reasonable effort to notify you of any delays.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">7. Website Use</h2>
          <p className="text-sm mb-3">By accessing our website, you agree to:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Use the site only for lawful purposes and in accordance with these Terms.</li>
            <li>Not attempt to gain unauthorized access to any part of the site or its related systems.</li>
            <li>Not copy, reproduce, or redistribute site content without our express written permission.</li>
          </ul>
          <p className="mt-3 text-sm">
            The content on this website (text, images, logos) is the property of Guardian Veterans LLC and is protected by applicable copyright laws.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">8. Indemnification</h2>
          <p className="text-sm">
            You agree to indemnify, defend, and hold harmless Guardian Veterans LLC, its officers, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys&apos; fees) arising out of your use of our services or website, your violation of these Terms, or your violation of any third-party rights.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">9. Governing Law</h2>
          <p className="text-sm">
            These Terms are governed by the laws of the State of Arizona, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of Maricopa County, Arizona.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">10. Changes to These Terms</h2>
          <p className="text-sm">
            We reserve the right to update these Terms at any time. Changes will be posted on this page with an updated date. Continued use of our services after changes are posted constitutes your acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">11. Contact Us</h2>
          <p className="text-sm mb-4">
            Questions about these Terms? We&apos;re happy to help:
          </p>
          <div className="bg-slate-50 rounded-lg p-5 text-sm space-y-2 border border-slate-200">
            <p className="font-semibold text-slate-900">Guardian Veterans LLC</p>
            <p>Waddell, AZ 85355</p>
            <p>
              Phone:{' '}
              <a href="tel:+16022956626" className="text-yellow-600 hover:underline">(602) 295-6626</a>
            </p>
            <p>
              Email:{' '}
              <a href="mailto:info@gvnemt.com" className="text-yellow-600 hover:underline">info@gvnemt.com</a>
            </p>
          </div>
        </section>

      </div>

      {/* Footer Strip */}
      <div className="bg-slate-900 text-slate-400 text-sm text-center py-6 px-4 border-t border-yellow-500/20">
        <span>© 2026 Guardian Veterans LLC · </span>
        <button onClick={() => navigate('/privacy-policy')} className="text-yellow-400 hover:underline cursor-pointer whitespace-nowrap">Privacy Policy</button>
        <span> · </span>
        <button onClick={() => navigate('/terms-of-service')} className="text-yellow-400 hover:underline cursor-pointer whitespace-nowrap">Terms of Service</button>
      </div>
    </div>
  );
}
